import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Edit, Trash2, Download, Share2, MessageCircle, ArrowLeft } from 'lucide-react'
import { useTabs } from '../../contexts/TabsContext'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import ChatWithNoteModal from '../common/ChatWithNoteModal'
import localStorageService from '../../services/localStorageService'
import toast from 'react-hot-toast'

/**
 * BaseNoteViewer Component
 * 
 * Generic base component that handles common note viewing functionality.
 * Provides common UI elements, note loading, decryption, and action handling.
 * Can be extended by specific note type viewers.
 */
const BaseNoteViewer = ({ 
  noteId, 
  tabId = null, 
  isInTab = false,
  onEdit,
  onDelete,
  onClose,
  onBack,
  className = '',
  customActions = null,
  children 
}) => {
  // State management
  const [note, setNote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isDecrypted, setIsDecrypted] = useState(false)
  const [showChatModal, setShowChatModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Context hooks
  const { openTab, updateTab, closeTab } = useTabs()
  const { notes, getCachedNoteById, deleteNote, fetchNotes } = useNotes()
  const { folders } = useFolders()
  const sensitiveSession = useSensitiveSession()
  const { isLocalMode, getSensitivePassword } = useLocalMode()
  const { openSettings } = useAuth()
  
  // Refs
  const hasLoadedNote = useRef(false)
  
  /**
   * Check if content appears to be encrypted
   */
  const isContentEncrypted = useCallback((content) => {
    if (!content) return false
    
    // Check if content looks like encrypted data (AES-GCM produces base64-like strings)
    const looksLikeHTML = content.includes('<') && content.includes('>')
    const looksLikePlainText = content.length < 100 && /^[a-zA-Z0-9\s.,!?-]+$/.test(content)
    const looksLikeEncrypted = content.length > 50 && /^[A-Za-z0-9+/]*={0,2}$/.test(content)
    
    // If it looks like encrypted data and doesn't look like HTML or plain text, it's likely encrypted
    return looksLikeEncrypted && !looksLikeHTML && !looksLikePlainText
  }, [])
  
  /**
   * Check if a note is sensitive (in sensitive folder)
   */
  const isSensitiveNote = useCallback((note) => {
    if (!note?.folderId) return false
    
    const noteFolder = folders.find(f => f.folderId === note.folderId)
    return noteFolder?.folderType === 'SENSITIVE'
  }, [folders])
  
  /**
   * Decrypt note content if needed (for sensitive notes)
   */
  const decryptNoteIfNeeded = useCallback(async (noteData) => {
    if (!noteData?.folderId) return noteData
    
    // Check if note is in a sensitive folder
    const noteFolder = folders.find(f => f.folderId === noteData.folderId)
    const isSensitive = noteFolder?.folderType === 'SENSITIVE'
    
    if (!isSensitive) {
      // For non-sensitive notes, check if they're encrypted and decrypt if needed
      const isEncrypted = noteData.isEncrypted
      
      if (!isEncrypted || !noteData.content) return noteData
      
      // For non-sensitive encrypted notes, try to decrypt if we have a session
      if (sensitiveSession.isAuthenticated) {
        const password = isLocalMode ? getSensitivePassword() : sensitiveSession.sessionPassword
        
        if (password) {
          try {
            const decryptedContent = localStorageService.decryptContent(noteData.content, password)
            console.log('✅ BaseNoteViewer: Non-sensitive note decrypted successfully')
            return { ...noteData, content: decryptedContent, isEncrypted: false }
          } catch (error) {
            console.error('❌ BaseNoteViewer: Failed to decrypt non-sensitive note:', error)
            return {
              ...noteData,
              content: '🔒 Failed to decrypt this note. Please try again.',
              isEncrypted: true
            }
          }
        }
      }
      
      return noteData
    }
    
    // For sensitive notes, always require password
    if (noteData.isEncrypted || isContentEncrypted(noteData.content)) {
      if (!sensitiveSession.isAuthenticated) {
        // Prompt for password
        const password = prompt('Enter password to decrypt this sensitive note:')
        if (!password) {
          throw new Error('Password required for sensitive note')
        }
        
        try {
          const decryptedContent = localStorageService.decryptContent(noteData.content, password)
          console.log('✅ BaseNoteViewer: Sensitive note decrypted successfully')
          return { ...noteData, content: decryptedContent, isEncrypted: false }
        } catch (error) {
          console.error('❌ BaseNoteViewer: Failed to decrypt sensitive note:', error)
          throw new Error('Invalid password')
        }
      } else {
        // Use session password
        const password = isLocalMode ? getSensitivePassword() : sensitiveSession.sessionPassword
        
        if (password) {
          try {
            const decryptedContent = localStorageService.decryptContent(noteData.content, password)
            console.log('✅ BaseNoteViewer: Sensitive note decrypted with session')
            return { ...noteData, content: decryptedContent, isEncrypted: false }
          } catch (error) {
            console.error('❌ BaseNoteViewer: Failed to decrypt sensitive note with session:', error)
            throw new Error('Session expired, please re-authenticate')
          }
        } else {
          throw new Error('No password available')
        }
      }
    }
    
    return noteData
  }, [folders, sensitiveSession, isLocalMode, getSensitivePassword, isContentEncrypted])
  
  /**
   * Load note data
   */
  const loadNote = useCallback(async () => {
    if (!noteId) {
      setError('No note ID provided')
      setLoading(false)
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      console.log('🔍 BaseNoteViewer: Loading note with ID:', noteId)
      
      // Try to get note from cache first
      let noteData = getCachedNoteById(noteId)
      
      if (!noteData) {
        // If not in cache, try to find in notes array
        noteData = notes.find(n => n.noteId === noteId || n.id === noteId)
      }
      
      if (!noteData) {
        setError('Note not found')
        setLoading(false)
        return
      }
      
      // Decrypt if needed
      const decryptedNote = await decryptNoteIfNeeded(noteData)
      
      setNote(decryptedNote)
      setIsDecrypted(!decryptedNote.isEncrypted && !isContentEncrypted(decryptedNote.content))
      
      // Update tab title if in tab mode
      if (isInTab && tabId && updateTab) {
        updateTab(tabId, { title: decryptedNote.title || 'Untitled Note' })
      }
      
      hasLoadedNote.current = true
      console.log('✅ BaseNoteViewer: Note loaded successfully:', decryptedNote.title)
      
    } catch (error) {
      console.error('❌ BaseNoteViewer: Failed to load note:', error)
      setError(error.message || 'Failed to load note')
    } finally {
      setLoading(false)
    }
  }, [noteId, getCachedNoteById, notes, decryptNoteIfNeeded, isInTab, tabId, updateTab, isContentEncrypted])
  
  /**
   * Action handlers
   */
  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit()
    } else {
      // Default edit behavior
      openTab({
        type: 'edit',
        noteId: noteId,
        title: note?.title || 'Untitled Note',
        note: note
      })
    }
  }, [onEdit, openTab, noteId, note])
  
  const handleDelete = useCallback(async () => {
    if (isDeleting) return
    
    if (onDelete) {
      onDelete()
    } else {
      // Default delete behavior
      if (window.confirm('Are you sure you want to delete this note?')) {
        setIsDeleting(true)
        try {
          await deleteNote(noteId)
          toast.success('Note deleted successfully')
          
          if (isInTab && tabId && closeTab) {
            closeTab(tabId)
          } else if (onClose) {
            onClose()
          } else if (onBack) {
            onBack()
          }
        } catch (error) {
          console.error('Failed to delete note:', error)
          toast.error('Failed to delete note')
        } finally {
          setIsDeleting(false)
        }
      }
    }
  }, [onDelete, deleteNote, noteId, isInTab, tabId, closeTab, onClose, onBack, isDeleting])
  
  const handleExport = useCallback(() => {
    if (!note) return
    
    try {
      // Create export content
      const exportContent = `# ${note.title || 'Untitled Note'}

**Created:** ${new Date(note.createdAt).toLocaleString()}
**Modified:** ${new Date(note.lastModified).toLocaleString()}
**Folder:** ${note.folderId ? folders.find(f => f.folderId === note.folderId)?.name || 'Unknown' : 'None'}

---

${note.content || 'No content available.'}`

      // Create and download file
      const blob = new Blob([exportContent], { type: 'text/markdown' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${note.title || 'note'}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      toast.success('Note exported successfully')
    } catch (error) {
      console.error('Failed to export note:', error)
      toast.error('Failed to export note')
    }
  }, [note, folders])
  
  const handleShare = useCallback(() => {
    if (!note) return
    
    // For now, just copy the note content to clipboard
    try {
      const shareText = `${note.title || 'Untitled Note'}\n\n${note.content || 'No content'}`
      navigator.clipboard.writeText(shareText)
      toast.success('Note content copied to clipboard')
    } catch (error) {
      console.error('Failed to share note:', error)
      toast.error('Failed to share note')
    }
  }, [note])
  
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else if (isInTab && tabId && closeTab) {
      closeTab(tabId)
    } else {
      window.history.back()
    }
  }, [onBack, isInTab, tabId, closeTab])
  
  // Effects
  useEffect(() => {
    if (!hasLoadedNote.current) {
      loadNote()
    }
  }, [loadNote])
  
  // Reset loaded flag when noteId changes
  useEffect(() => {
    hasLoadedNote.current = false
  }, [noteId])
  
  // Render loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  // Render error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={loadNote}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }
  
  // Render not found state
  if (!note) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <p className="text-gray-500 mb-4">Note not found</p>
          <button 
            onClick={handleBack}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }
  
  // Get folder info
  const noteFolder = note.folderId ? folders.find(f => f.folderId === note.folderId) : null
  
  return (
    <div className={`flex flex-col h-full bg-white dark:bg-gray-800 ${className}`} style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Note Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                {note.title || 'Untitled Note'}
              </h1>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                <span>Created: {new Date(note.createdAt).toLocaleDateString()}</span>
                <span>Modified: {new Date(note.lastModified).toLocaleDateString()}</span>
                {noteFolder && (
                  <span className="flex items-center space-x-1">
                    <span>Folder:</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      noteFolder.folderType === 'SENSITIVE' 
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    }`}>
                      {noteFolder.name}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Custom Actions */}
            {customActions}
            
            {/* Default Actions - only show if no custom actions */}
            {!customActions && (
              <>
                <button
                  onClick={() => setShowChatModal(true)}
                  className="p-2 rounded-full text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                  title="Chat with note"
                >
                  <MessageCircle className="h-5 w-5" />
                </button>
                
                <button
                  onClick={handleExport}
                  className="p-2 rounded-full text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  title="Export note"
                >
                  <Download className="h-5 w-5" />
                </button>
                
                <button
                  onClick={handleShare}
                  className="p-2 rounded-full text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                  title="Share note"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </>
            )}
            
            {/* Edit button - always show */}
            <button
              onClick={handleEdit}
              className="p-2 rounded-full text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title="Edit note"
            >
              <Edit className="h-5 w-5" />
            </button>
            
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-2 rounded-full text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete note"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div 
        className="flex-1 overflow-hidden" 
        style={{ 
          minHeight: '500px',
          height: 'calc(-120px + 100vh)', // Subtract header height
          width: '100%'
        }}
      >
        {children || (
          <div className="p-4 text-gray-500 dark:text-gray-400 italic text-center">
            No content renderer provided
          </div>
        )}
      </div>
      
      {/* Chat Modal */}
      {showChatModal && (
        <ChatWithNoteModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          note={note}
        />
      )}
    </div>
  )
}

export default BaseNoteViewer
